<template>
  <v-container :key="identifier" fluid class="pa-0" style="height: inherit">

    <v-progress-linear v-if="entityLoading" absolute indeterminate color="tertiary" height="2">
    </v-progress-linear>
    <v-progress-linear v-else-if="entitySilentLoading" absolute indeterminate color="primary" height="2">
    </v-progress-linear>

    <EmptyPage v-if="error" :icon="'mdi-file-question'"
      :title="$t('servicesystem.common_objects.fallbackterms.no_documents')"
      :message="$t('servicesystem.common_objects.fallbackterms.found_no_documents')">
      <template v-slot:actions>
        <v-btn @click="refreshItem()">Refresh</v-btn> <v-btn>Report issue</v-btn>
        <v-card dark color="error" width="500">
          Error: {{ error }}
        </v-card>
      </template>
    </EmptyPage>

    <v-card v-else-if="debug" outlined class="ma-4 pa-4">
      <h5 class="mb-2">ViewEntityContainer</h5>
      <b>Item:</b><br>
      <pre>{{ item }}</pre>
      <b>Last notification:</b><br>
      <pre>{{ lastValidNotification }}</pre>
    </v-card>

    <router-view v-else :containerItem="item" :containerInitLoading="entityLoading"
      :containerLoading="entitySilentLoading" @Refresh="refreshItem()" @SetContainerItem="setItem">
    </router-view>

  </v-container>
</template>


<script>
import EmptyPage from "@/components/Sections/EmptyPage.vue";

export default {
  name: "ViewEntityContainer",
  props: {
    entityName: { //used as a key in the store
      type: String,
      required: true,
    },
    identifier: { //used as refresh trigger on change and passed as a parameter to getMethod
      type: [Number, String],
      required: true,
    },
    getMethod: { //function to get the entity from the backend
      type: Function,
      required: true,
    },
    refreshOnTimestamp: { //option to trigger refresh on timestamp query parameter change
      type: Boolean,
      default: false,
    },
    notificationListeners: { //option to listen to signalr notifications
      type: Array,
      default: () => [],
    },
    notificationValidator: {  //filter validator for signalr notifications, should return true/false based on if the notification should trigger refresh
      type: Function,
      default: () => true,
    },
    debug: { //shows enity as a pre tag instead of router-view
      type: Boolean,
      default: false,
    },
  },
  components: {
    EmptyPage,
  },
  mixins: [],

  data() {
    return {
      error: null,
      lastValidNotification: null,
      currentRequestSeq: 0,
    };
  },

  mounted() {
    if (this.item && this.item.Id != this.identifier) {
      this.setItem(null);
    }

    this.refreshItem(false).then(
      () => {
        this.initiateListeners();
      }
    );
  },

  beforeDestroy() {
    this.notificationListeners.forEach(event => {
      this.$store.dispatch("NotificationStore/unsubscribe", [event, this.notifyChanges]);
    });
  },

  methods: {
    initiateListeners() {
      this.notificationListeners.forEach(event => {
        this.$store.dispatch("NotificationStore/subscribe", [event, this.notifyChanges]);
      });
    },

    isAbort(err) {
      return err?.name === 'AbortError' || err?.code === 'ERR_CANCELED' || /aborted/i.test(err?.message || '');
    },

    notifyChanges(args) {
      if (this.notificationValidator(args, this.identifier)) {
        //console.log('valid notification', args);
        this.lastValidNotification = args;
        this.refreshItem();
      }
    },

    async refreshItem(silent = true) {
      const seq = ++this.currentRequestSeq; // mark this call as the latest
      silent ? this.entitySilentLoading = true : this.entityLoading = true;
      this.error = null;
      
      try {
        const response = await this.getMethod(this.identifier);
        // If another fetch started after this one, bail without touching state
        if (seq !== this.currentRequestSeq) {
          return;
        }
        this.setItem(response);
      }
      catch (e) {
        // Ignore aborts completely
        if (this.isAbort(e)) {
          return;
        }
        // Only the latest request may flip success/error state
        if (seq === this.currentRequestSeq) {
          this.error = e;
          console.error('fetch error', e);
        }
      }
      finally {
        // Only the latest request may end the loading state
        if (seq === this.currentRequestSeq) {
          this.entitySilentLoading = false;
          this.entityLoading = false;
        }
      }
    },

    setItem(obj) {
      this.item = obj;
    }
  },

  computed: {
    item: {
      get() {
        return this.$store.getters["entitycontainerstore/getEntity"](this.entityName);
      },
      set(obj) {
        this.$store.dispatch("entitycontainerstore/setEntity", { entityName: this.entityName, entity: obj });
      },
    },

    entityLoading: {
      get() {
        return this.$store.getters["entitycontainerstore/getEntityLoading"](this.entityName);
      },
      set(value) {
        this.$store.dispatch("entitycontainerstore/setEntityLoading", { entityName: this.entityName, value: value, silent: false });
      },
    },

    entitySilentLoading: {
      get() {
        return this.$store.getters["entitycontainerstore/getEntitySilentLoading"](this.entityName);
      },
      set(value) {
        this.$store.dispatch("entitycontainerstore/setEntityLoading", { entityName: this.entityName, value: value, silent: true });
      },
    },
  },

  watch: {
    identifier: function () {
      this.refreshItem();
    },
    "$route.name"() { //refreshes item when changing tabs
      this.refreshItem();
    },
    "$route.query.timestamp"() {
      if (this.refreshOnTimestamp) {
        this.refreshItem();
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>